import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, map, Observable, of, switchMap, take, tap } from "rxjs";
import { BaseRespone, PaginationResult } from "src/app/models/common.response";
import { environment } from "src/environments/environment";
import { IPatientInfo, IPatientRequest } from "./ipatient-info";


@Injectable({ providedIn: 'root' })
export class PatientService {

    private _patient: BehaviorSubject<IPatientInfo | null> = new BehaviorSubject<IPatientInfo | null>(null);
    private _patients: BehaviorSubject<IPatientInfo[]> = new BehaviorSubject<IPatientInfo[]>([]);
    private _pagination: BehaviorSubject<PaginationResult | null> = new BehaviorSubject<PaginationResult | null>(null);

    constructor(
        private _httpClient: HttpClient
    ) { }

    get patient$(): Observable<IPatientInfo | null> {
        return this._patient.asObservable();
    }

    get patients$(): Observable<IPatientInfo[]> {
        return this._patients.asObservable();
    }

    get pagination$(): Observable<PaginationResult | null> {
        return this._pagination.asObservable();
    }



    getPatients(patientdata: IPatientRequest): Observable<BaseRespone<{ pagination: PaginationResult; patients: IPatientInfo[] }>> {
        return this._httpClient.post<BaseRespone<{ pagination: PaginationResult; patients: IPatientInfo[] }>>(`${environment.baseURl}/Patient/list`, patientdata).pipe(
            tap((response) => {
                this._pagination.next(response.result.pagination);
                this._patients.next(response.result.patients);
            }),
            catchError((error) => {
                console.error('Error to Fetching data:', error);
                throw error;
            })
        );
    }

    createPatient(patient: FormData): Observable<BaseRespone<IPatientInfo>> {
        return this._httpClient.post<BaseRespone<IPatientInfo>>(`${environment.baseURl}/Patient`, patient)
            .pipe(
                catchError((error) => {
                    return of(error.error);
                }),
                map((newpatients) => {
                    // Return the new patients
                    return newpatients;
                })
            )
    }

    deletePatient(id: number): Observable<BaseRespone<boolean>> {
        return this.patients$.pipe(
            take(1),
            switchMap(patient =>
                this._httpClient.delete<BaseRespone<boolean>>(`${environment.baseURl}/Patient/${id}`).pipe(
                    map((deletedResponse) => {
                        if (deletedResponse.result) {
                            const updatedpatient = patient.filter(item => item.id !== id);
                            this._patients.next(updatedpatient);
                        }
                        return deletedResponse;
                    })
                )
            ), catchError((error) => {
                return of(error.error);
            })
        );
    }

    updatePatient(patient: FormData): Observable<BaseRespone<IPatientInfo>> {
        return this.patients$.pipe(
            take(1),
            switchMap(patients => this._httpClient.put<BaseRespone<IPatientInfo>>(`${environment.baseURl}/Patient`, patient).pipe(
                map((updated: BaseRespone<IPatientInfo>) => {
                    const patientid = patient.get('id');
                    const index = patients.findIndex(item => item.id === Number(patientid));

                    if (index !== -1) {
                        patients[index] = { ...patients[index], ...updated.result }; // ✅ Update safely
                        this._patients.next(patients);
                    }

                    // Return the updated product
                    return updated;
                })
            )), catchError((error) => {
                return of(error.error);
            })
        );
    }

    getPatientByID(id: Number) {
        return this._httpClient.get<BaseRespone<IPatientInfo>>(`${environment.baseURl}/Patient/${id}`).pipe(
            tap((response) => {
                this._patient.next(response.result);
            }),
            catchError((error) => {
                console.error('Error to Fetching data:', error);
                throw error;
            })
        );
    }

}
